/** Canonical OIDC settings — must stay aligned across main.tsx, teamsAuthFlow, and localStorage keys. */

const OIDC_STORAGE_PREFIX = 'oidc.user:';

/** Strip trailing slashes so storage keys stay stable across deploys. */
export function normalizeOidcAuthority(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

export function getOidcAuthority(): string {
  const env = import.meta.env.VITE_AUTH_AUTHORITY?.trim();
  if (env) return normalizeOidcAuthority(env);
  if (typeof window !== 'undefined') {
    return normalizeOidcAuthority(`${window.location.origin}/ocid`);
  }
  return 'http://localhost:5173/ocid';
}

export function getOidcClientId(): string {
  return import.meta.env.VITE_AUTH_CLIENT_ID?.trim() || 'connectwise-sidekick';
}

export function getOidcScope(): string {
  return 'openid profile email roles approles offline_access';
}

/** Full localStorage key used by WebStorageStateStore (`oidc.` + `user:{authority}:{client_id}`). */
export function getOidcUserStorageKey(authority?: string, clientId?: string): string {
  const auth = authority ?? getOidcAuthority();
  const cid = clientId ?? getOidcClientId();
  return `${OIDC_STORAGE_PREFIX}${auth}:${cid}`;
}

/** Legacy keys from trailing-slash authority or raw env drift. */
export function getLegacyOidcUserStorageKeys(): string[] {
  const canonical = getOidcUserStorageKey();
  const auth = getOidcAuthority();
  const cid = getOidcClientId();
  const keys = new Set<string>();

  keys.add(`${OIDC_STORAGE_PREFIX}${auth}/:${cid}`);

  const rawEnv = import.meta.env.VITE_AUTH_AUTHORITY?.trim();
  if (rawEnv) {
    keys.add(`${OIDC_STORAGE_PREFIX}${rawEnv}:${cid}`);
    keys.add(`${OIDC_STORAGE_PREFIX}${normalizeOidcAuthority(rawEnv)}:${cid}`);
  }

  keys.delete(canonical);
  return [...keys];
}

/** Copy user JSON from a legacy storage key into the canonical key (one-time per session). */
export function migrateLegacyOidcUserStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const canonicalKey = getOidcUserStorageKey();
  if (localStorage.getItem(canonicalKey)) return;

  for (const legacyKey of getLegacyOidcUserStorageKeys()) {
    const value = localStorage.getItem(legacyKey);
    if (!value) continue;
    localStorage.setItem(canonicalKey, value);
    localStorage.removeItem(legacyKey);
    console.info('[oidc] Migrated user storage from legacy key:', legacyKey);
    return;
  }
}

export function getOidcRedirectUri(origin = typeof window !== 'undefined' ? window.location.origin : ''): string {
  return `${origin}/callback`;
}
